import {
  ChartLineUpIcon,
  ClockCountdownIcon,
  CloudCheckIcon,
  DevicesIcon,
  ExamIcon,
  RankingIcon,
  ShieldCheckIcon,
  StackIcon,
  TranslateIcon,
  WifiSlashIcon,
  type Icon,
} from '@phosphor-icons/react';

/*
  Website copy. Used by the page itself and by the build-time pre-render (llms.txt and the FAQ
  structured data), so search engines and AI assistants read exactly what visitors read.
  Only describe features that exist; no invented numbers, testimonials, prices or contacts.
*/

export const SITE = {
  name: 'Testpio',
  summary:
    'Online test series platform for coaching institutes in India. Students take exam-style mock tests on their own phones in Hindi and English, and get ranks, percentiles, solutions and progress reports.',
};

export const NAV = [
  { href: '#features', label: 'Features' },
  { href: '#how-it-works', label: 'How it works' },
  { href: '#students', label: 'For students' },
  { href: '#faq', label: 'FAQ' },
];

/** Ready-made patterns (packages/shared/src/templates.ts). */
export const PATTERNS = ['SSC CGL / CHSL Tier 1', 'SSC GD Constable', 'IBPS / SBI PO Prelims', 'IBPS / SBI Clerk Prelims', 'RRB NTPC CBT 1', 'RRB Group D', 'BPSC Prelims'];

export const FEATURES: { icon: Icon; title: string; text: string }[] = [
  {
    icon: ExamIcon,
    title: 'A real exam screen',
    text: 'Question palette, mark for review, sections and a server-controlled timer, the way SSC and banking computer-based tests work. Keyboard shortcuts on computers.',
  },
  {
    icon: TranslateIcon,
    title: 'Hindi and English',
    text: 'Write each question in Hindi, English or both. Students switch language with one tap, even in the middle of a test.',
  },
  {
    icon: WifiSlashIcon,
    title: 'Made for weak networks',
    text: 'Answers are kept on the phone and sent when the network returns. A refresh, a call or a dead battery does not lose a single answer.',
  },
  {
    icon: RankingIcon,
    title: 'Fair ranks and percentiles',
    text: 'Ties ranked the way official results are, percentiles, batch ranks and a leaderboard. You decide when results go out.',
  },
  {
    icon: ChartLineUpIcon,
    title: 'Solutions and progress',
    text: 'Correct answers and explanations after results, accuracy by topic, and progress across every test a student takes.',
  },
  {
    icon: StackIcon,
    title: 'Quick question entry',
    text: 'Type or paste questions, paste rows from Excel, or upload screenshots from books and PDFs. Text typed in Kruti Dev is flagged before students see it.',
  },
];

export const STEPS = [
  {
    title: 'Add your questions',
    text: 'Type them, paste rows from Excel or upload screenshots. Tag them by subject and topic to build your own question bank.',
  },
  {
    title: 'Schedule a test',
    text: 'Start from a pattern like SSC CGL, choose batches and a time, and share the link in your WhatsApp group.',
  },
  {
    title: 'Release ranks and solutions',
    text: 'Students write the test on their phones. Check the answer key, then release ranks, percentiles and solutions together.',
  },
];

export const STUDENT_POINTS = [
  ['Rank and percentile', ' among everyone who took the test, plus a batch rank'],
  ['Solutions and explanations', ' for every question'],
  ['Strong and weak topics', ', and progress across tests'],
  ['Badges and a result card', ' to share on WhatsApp'],
] as const;

export const TEACHER_POINTS = [
  ['Live monitor', ': who is writing, who has submitted and time left. Give extra time in one click.'],
  ['Students who need attention', ': missed tests, falling percentile or bottom 20%.'],
  ['Answer key check', ': flags questions where most top scorers chose a different option.'],
  ['Excel export', ' of results and a results poster for your WhatsApp group.'],
] as const;

export const TRUST: { icon: Icon; title: string; text: string }[] = [
  { icon: ClockCountdownIcon, title: 'The server keeps time', text: "Changing the phone's clock adds no time, and answers sent after the deadline are not accepted." },
  { icon: DevicesIcon, title: 'One device at a time', text: 'A test can be open on only one phone or computer at once.' },
  { icon: CloudCheckIcon, title: 'Every answer confirmed', text: 'An answer counts as saved only after the server has stored it.' },
  { icon: ShieldCheckIcon, title: 'Each institute separate', text: 'Your questions and students are visible only to your institute. Answer keys never reach students before results.' },
];

export const FAQ = [
  {
    q: 'Do students need to install an app?',
    a: 'No. Tests open in the phone’s browser from your institute’s link. Students can add it to their home screen and open it like an app.',
  },
  {
    q: 'What if the internet goes off during a test?',
    a: 'Students keep answering. Answers are kept on the phone and sent automatically when the connection returns. The timer runs on the server, so it stays correct.',
  },
  {
    q: 'Can we use our own questions?',
    a: 'Yes. Type or paste them, paste rows from Excel or Google Sheets, or upload screenshots from books and PDFs. Each question can be in Hindi, English or both.',
  },
  {
    q: 'Which exam patterns can we use?',
    a: 'Ready-made patterns for SSC, banking, railways and BPSC come with their sections, marks and negative marking. You can also set your own sections, marks, negative marking and duration for any test.',
  },
  {
    q: 'When do students see their rank?',
    a: 'For ranked tests, when you release the results, so you can check the answer key first. Practice tests show the result as soon as the student submits.',
  },
  {
    q: 'Can students cheat by changing the time or opening the test twice?',
    a: 'No. The server controls the time, a test can be open on only one device at a time, and the order of questions and options can be shuffled for each student.',
  },
];
